import React, { useEffect, useState } from 'react';
import { Card, Typography, Row, Col, Button, Badge, Space, Skeleton, Statistic, Alert, Modal, List, Tag, message } from 'antd';
import { 
  PlusOutlined,
  ToolOutlined,
  UserAddOutlined,
  FileTextOutlined,
  ShoppingCartOutlined,
  BellOutlined,
  CalendarOutlined,
  WarningOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  PhoneOutlined,
  CheckOutlined,
  RightOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { dashboardService, QuickStats } from '../../services/dashboardService';
import { useAuth } from '../../contexts/AuthContext';

const { Text, Title } = Typography;

interface QuickAction {
  icon: React.ReactNode;
  label: string;
  path: string;
  color: string;
  badge?: number;
  onClick?: () => void;
}

const QuickActionsWidget: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [stats, setStats] = useState<QuickStats | null>(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager';

  // Modal des demandes de rappel hors-horaires
  const [callbacksModalOpen, setCallbacksModalOpen] = useState(false);
  const [pendingCallbacks, setPendingCallbacks] = useState<any[]>([]);
  const [loadingCallbacks, setLoadingCallbacks] = useState(false);
  const [resolvingId, setResolvingId] = useState<number | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await dashboardService.getQuickStats();
      setStats(data);
    } catch (err) {
      console.error('Erreur chargement quick stats:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenCallbacksModal = async () => {
    setCallbacksModalOpen(true);
    setLoadingCallbacks(true);
    try {
      const data = await dashboardService.getPendingCallbacks();
      setPendingCallbacks(data);
    } catch (e) {
      console.error('Erreur lors du chargement des rappels:', e);
      message.error('Impossible de charger les demandes de rappel');
    } finally {
      setLoadingCallbacks(false);
    }
  };

  const handleResolveCallback = async (id: number, customerName: string) => {
    setResolvingId(id);
    try {
      await dashboardService.resolveCallback(id);
      message.success(`Demande de rappel pour ${customerName} marquée comme effectuée !`);
      setPendingCallbacks(prev => prev.filter(c => c.id !== id));
      // Mettre à jour les stats du dashboard
      if (stats) {
        setStats({
          ...stats,
          pendingContactRequests: Math.max(0, (stats.pendingContactRequests || 1) - 1)
        });
      }
    } catch (e) {
      console.error('Erreur lors du traitement du rappel:', e);
      message.error('Erreur lors de la mise à jour');
    } finally {
      setResolvingId(null);
    }
  };

  const pendingCallsCount = stats?.pendingContactRequests || 0;

  const quickActions: QuickAction[] = [
    {
      icon: <PlusOutlined />,
      label: 'Nouvelle Intervention',
      path: '/interventions',
      color: '#1890ff'
    },
    {
      icon: <UserAddOutlined />,
      label: 'Nouveau Client',
      path: '/customers/new',
      color: '#52c41a'
    },
    {
      icon: <FileTextOutlined />,
      label: 'Nouveau Devis',
      path: '/devis',
      color: '#722ed1'
    },
    {
      icon: <PhoneOutlined />,
      label: 'Demandes de Rappel',
      path: '/demandes-rappel?status=pending',
      color: '#0a543d',
      badge: pendingCallsCount,
      onClick: () => handleOpenCallbacksModal()
    }
  ];

  if (isAdmin) {
    quickActions.push({
      icon: <BellOutlined />,
      label: 'Envoyer Notification',
      path: '/broadcast-notification',
      color: '#fa8c16'
    });
  }

  if (loading) {
    return (
      <Card style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Skeleton active paragraph={{ rows: 3 }} />
      </Card>
    );
  }

  return (
    <>
      <Card
        title={
          <Space>
            <CalendarOutlined style={{ color: '#1890ff' }} />
            <span>Actions Rapides & Résumé</span>
          </Space>
        }
        style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
      >

        {/* Quick Stats Summary */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#e6f7ff', borderRadius: '8px' }}>
              <CalendarOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }} />
              <Statistic 
                title={<Text type="secondary" style={{ fontSize: '12px' }}>Interventions aujourd'hui</Text>}
                value={stats?.interventions.today || 0}
                valueStyle={{ fontSize: '20px', color: '#1890ff' }}
              />
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#fff7e6', borderRadius: '8px' }}>
              <ClockCircleOutlined style={{ fontSize: 24, color: '#fa8c16', marginBottom: 8 }} />
              <Statistic 
                title={<Text type="secondary" style={{ fontSize: '12px' }}>En attente d'assignation</Text>}
                value={stats?.interventions.pendingAssignment || 0}
                valueStyle={{ fontSize: '20px', color: '#fa8c16' }}
              />
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f6ffed', borderRadius: '8px' }}>
              <ToolOutlined style={{ fontSize: 24, color: '#52c41a', marginBottom: 8 }} />
              <Statistic 
                title={<Text type="secondary" style={{ fontSize: '12px' }}>En cours</Text>}
                value={stats?.interventions.inProgress || 0}
                valueStyle={{ fontSize: '20px', color: '#52c41a' }}
              />
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#fff1f0', borderRadius: '8px' }}>
              <WarningOutlined style={{ fontSize: 24, color: '#ff4d4f', marginBottom: 8 }} />
              <Statistic 
                title={<Text type="secondary" style={{ fontSize: '12px' }}>Réclamations ouvertes</Text>}
                value={stats?.openComplaints || 0}
                valueStyle={{ fontSize: '20px', color: '#ff4d4f' }}
              />
            </div>
          </Col>
        </Row>

        {/* Divider */}
        <div style={{ borderTop: '1px solid #f0f0f0', marginBottom: 16 }} />

        {/* Quick Action Buttons */}
        <Title level={5} style={{ marginBottom: 16 }}>
          Actions Rapides
        </Title>
        <Row gutter={[12, 12]}>
          {quickActions.map((action, index) => (
            <Col xs={12} sm={6} key={index}>
              <Badge count={action.badge} offset={[-5, 5]}>
                <Button
                  type="default"
                  block
                  style={{
                    height: 'auto',
                    padding: '16px 8px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderColor: action.color,
                    transition: 'all 0.3s'
                  }}
                  onClick={() => action.onClick ? action.onClick() : navigate(action.path)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = action.color;
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fff';
                    e.currentTarget.style.color = 'inherit';
                  }}
                >
                  <span style={{ fontSize: 24, marginBottom: 8, color: action.color }}>
                    {action.icon}
                  </span>
                  <Text style={{ fontSize: '12px' }}>{action.label}</Text>
                </Button>
              </Badge>
            </Col>
          ))}
        </Row>

        {/* Additional Stats Row */}
        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          <Col xs={12}>
            <Button 
              type="text" 
              block 
              style={{ 
                textAlign: 'left', 
                height: 'auto', 
                padding: '8px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px'
              }}
              onClick={() => navigate('/commandes')}
            >
              <Space>
                <ShoppingCartOutlined style={{ color: '#722ed1', fontSize: 18 }} />
                <div>
                  <Text strong>{stats?.pendingOrders || 0}</Text>
                  <Text type="secondary" style={{ marginLeft: 4 }}>Commandes en attente</Text>
                </div>
              </Space>
            </Button>
          </Col>
          <Col xs={12}>
            <Button 
              type="text" 
              block 
              style={{ 
                textAlign: 'left', 
                height: 'auto', 
                padding: '8px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px'
              }}
              onClick={() => navigate('/clients')}
            >
              <Space>
                <TeamOutlined style={{ color: '#52c41a', fontSize: 18 }} />
                <div>
                  <Text strong>+{stats?.newCustomers || 0}</Text>
                  <Text type="secondary" style={{ marginLeft: 4 }}>Nouveaux clients (mois)</Text>
                </div>
              </Space>
            </Button>
          </Col>
        </Row>
      </Card>

      {/* MODAL SIGNALEMENT RAPPELS CLIENTS HORS-HORAIRES */}
      <Modal
        title={
          <Space>
            <PhoneOutlined style={{ color: '#0a543d' }} />
            <span style={{ fontWeight: 700 }}>Demandes de Rappel Client (Hors-horaires)</span>
          </Space>
        }
        open={callbacksModalOpen}
        onCancel={() => setCallbacksModalOpen(false)}
        footer={[
          <Button key="all" icon={<RightOutlined />} onClick={() => { setCallbacksModalOpen(false); navigate('/demandes-rappel?status=pending'); }}>
            Voir toutes les demandes de rappel
          </Button>,
          <Button key="close" type="primary" style={{ backgroundColor: '#0a543d', borderColor: '#0a543d' }} onClick={() => setCallbacksModalOpen(false)}>
            Fermer
          </Button>
        ]}
        width={720}
      >
        <div style={{ marginBottom: 12, color: '#64748b', fontSize: 13 }}>
          Ces clients ont déposé une demande de rappel. Appelez le client et cliquez sur <b>« Signaler comme Rappelé »</b> pour enregistrer le traitement lors de la reprise.
        </div>

        <List
          loading={loadingCallbacks}
          dataSource={pendingCallbacks}
          locale={{ emptyText: 'Aucune demande de rappel en attente' }}
          renderItem={(item: any) => {
            const customerName = item.customer 
              ? `${item.customer.first_name || ''} ${item.customer.last_name || ''}`.trim()
              : item.name || 'Client';
            const phone = item.phone || item.customer?.user?.phone || '';
            
            return (
              <List.Item
                style={{
                  padding: 16,
                  border: '1px solid #e2e8f0',
                  borderRadius: 12,
                  marginBottom: 12,
                  backgroundColor: '#ffffff'
                }}
                actions={[
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    loading={resolvingId === item.id}
                    style={{ backgroundColor: '#16a34a', borderColor: '#16a34a', borderRadius: 8, fontWeight: 600 }}
                    onClick={() => handleResolveCallback(item.id, customerName)}
                  >
                    Signaler comme Rappelé
                  </Button>
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space size="middle" wrap>
                      <Text strong style={{ fontSize: 15 }}>{customerName}</Text>
                      {phone && (
                        <a href={`tel:${phone.replace(/\s+/g, '')}`} style={{ color: '#0a543d', fontWeight: 600, fontSize: 13 }}>
                          📞 {phone}
                        </a>
                      )}
                      <Tag color="volcano" style={{ borderRadius: 10 }}>
                        {item.motive || 'Demande de rappel'}
                      </Tag>
                      <Tag color="blue" style={{ borderRadius: 10 }}>
                        {item.preferred_time === 'morning' ? 'Créneau: Matin' : item.preferred_time === 'afternoon' ? 'Créneau: Après-midi' : 'Flexible'}
                      </Tag>
                    </Space>
                  }
                  description={
                    <div style={{ marginTop: 6 }}>
                      <div style={{ color: '#334155', fontStyle: 'italic', marginBottom: 4 }}>
                        « {item.message || 'Souhaite être rappelé.'} »
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        Reçu le {item.created_at ? new Date(item.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : 'Récemment'}
                      </div>
                    </div>
                  }
                />
              </List.Item>
            );
          }}
        />
      </Modal>
    </>
  );
};

export default QuickActionsWidget;

